import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Trash2, Loader2, ImageIcon } from "lucide-react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { photoSrc } from "@/lib/photos";

// Admin-facing photo moderation: view + reject (soft-remove) property & room photos.
export default function AdminPhotoModeration({ propertyId }) {
  const [propPhotos, setPropPhotos] = useState([]);
  const [roomPhotos, setRoomPhotos] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      apiClient.get(`/properties/${propertyId}/photos`).then((r) => setPropPhotos(r.data || [])).catch(() => {}),
      apiClient.get(`/properties/${propertyId}/room-photos`).then((r) => setRoomPhotos(r.data || {})).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const reject = async (pid) => {
    if (!window.confirm("Tolak/hapus foto ini? Foto tidak akan tampil ke customer.")) return;
    setBusy(pid);
    try {
      await apiClient.delete(`/admin/photos/${pid}`);
      toast.success("Foto ditolak.");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const roomEntries = Object.entries(roomPhotos);
  const isEmpty = propPhotos.length === 0 && roomEntries.length === 0;

  const Thumb = ({ ph, tid }) => (
    <div className="relative rounded-xl overflow-hidden border border-border aspect-[4/3]" data-testid={tid}>
      <img src={photoSrc(ph)} alt="foto" className="w-full h-full object-cover" />
      <button type="button" onClick={() => reject(ph.id)} disabled={busy === ph.id}
        data-testid={`${tid}-reject`}
        className="absolute top-1.5 right-1.5 p-1.5 rounded-lg bg-white/90 text-destructive shadow disabled:opacity-50" title="Tolak foto">
        {busy === ph.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
      </button>
    </div>
  );

  return (
    <div data-testid="admin-photo-moderation">
      {loading ? (
        <div className="py-8 grid place-items-center"><Loader2 className="animate-spin text-primary" size={22} /></div>
      ) : isEmpty ? (
        <div className="rounded-xl border border-dashed border-border bg-secondary/30 p-8 text-center" data-testid="admin-photos-empty">
          <ImageIcon size={24} className="mx-auto mb-2 text-primary" />
          <p className="text-sm text-muted-foreground">Belum ada foto pada property ini.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {propPhotos.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground mb-2">Foto Property</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {propPhotos.map((ph, i) => <Thumb key={ph.id} ph={ph} tid={`admin-photo-item-${i}`} />)}
              </div>
            </div>
          )}
          {roomEntries.map(([roomId, list], ri) => (
            <div key={roomId}>
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground mb-2">Foto Kamar {list[0]?.room_number ? "" : ""}</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {list.map((ph, i) => <Thumb key={ph.id} ph={ph} tid={`admin-room-photo-${ri}-${i}`} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
